import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../store/actions/userActions";
import { initialsOf } from "../../ForBrands/brandDealUtils";
import IndexShell from "./IndexShell";

// Player Index shell: the same 72px topbar and fit-to-width frame the Brand,
// Athlete, Coach, Rep and Admin V2 shells use (IndexShell, shared with the
// public index), over the athlete index every business role shares ("/" and
// /business/dashboard). The index is a single page, so there is no tab bar;
// the avatar menu carries the role's other surfaces (the links the old
// navbar's avatar menu offered). The shell holds no index state (filters,
// counts and paging live in AllAthletes), so it frames any page the same
// way: the account's password, profile and billing pages at
// /business/password, /business/profile and /business/billing sit in the
// same gutter. It takes its page as children, or as a nested route's Outlet.

export const INDEX_HOME_PATH = "/business/dashboard";
export const INDEX_PASSWORD_PATH = "/business/password";
export const INDEX_PROFILE_PATH = "/business/profile";
export const INDEX_BILLING_PATH = "/business/billing";

const has = (perms, p) => perms.includes(p);

export const menuLinksFor = (permissions = [], email = "") => {
  const links = [];
  const isAdmin = has(permissions, "IS_ADMIN");
  const isStaff = has(permissions, "IS_CACHE_AI");
  if (isAdmin) {
    links.push(
      { to: "/cacheshare-tool", label: "CacheShare tool" },
      { to: "/brand/home", label: "Brand Mode" },
      { to: "/users", label: "Users" },
      { to: "/admin/team-management", label: "Teams" },
      { to: "/admin/payments", label: "Payments" },
      { to: "/admin/api-keys", label: "API keys" },
      { to: "/admin/dependencies", label: "Dependencies" },
      { to: "/admin/logs", label: "Logs" },
    );
    if (email === "admin@cache-ai.com") links.push({ to: "/admin/codes", label: "Codes" });
  } else if (isStaff) {
    links.push({ to: "/brand/home", label: "Brand Mode" }, { to: "/users", label: "Users" });
  }
  if (has(permissions, "IS_COACH")) {
    links.push(
      { to: "/coach/home", label: "Coach Mode" },
      { to: "/coach/cacheshare", label: "CacheShare" },
      { to: "/coach/profile", label: "Profile" },
      { to: "/coach/circle", label: "My Circle" },
      { to: "/coach/billing", label: "Billing" },
    );
  }
  if (has(permissions, "IS_BRAND_AGENCY") && !isAdmin && !isStaff) {
    links.push({ to: "/brand/home", label: "Brand Mode" });
  }
  // The account's own profile and billing pages. Coaches have both in Coach
  // Mode (above); admins get the profile only; a pure brand's /business
  // links go on to Brand Mode.
  const paysForPlan = has(permissions, "IS_BRAND_AGENCY") || has(permissions, "IS_RECRUITER_SCOUT") || has(permissions, "IS_AD_PD");
  if ((paysForPlan || isAdmin) && !has(permissions, "IS_COACH")) {
    links.push({ to: INDEX_PROFILE_PATH, label: "Profile" });
  }
  if (paysForPlan && !has(permissions, "IS_COACH")) {
    links.push({ to: INDEX_BILLING_PATH, label: "Billing" });
  }
  if (has(permissions, "IS_REP")) links.push({ to: "/rep/home", label: "Rep Mode" });
  const seen = new Set();
  return links.filter((l) => (seen.has(l.to) ? false : seen.add(l.to)));
};

// Where the CacheShare pill in the topbar goes: admins keep the full tool,
// coaches get their Coach Mode CacheShare.
export const cacheShareLinkFor = (permissions = []) => {
  if (has(permissions, "IS_ADMIN")) return "/cacheshare-tool";
  if (has(permissions, "IS_COACH")) return "/coach/cacheshare";
  return null;
};

const displayName = (user) => {
  const first = user?.firstName || (user?.name || "").split(" ")[0] || "";
  const last = user?.lastName || user?.surname || ((user?.name || "").split(" ").slice(1).join(" ") || "");
  const full = `${first} ${last}`.trim();
  return full || user?.email || "Account";
};

const AvatarMenu = ({ user, permissions }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const name = displayName(user);
  const initials = initialsOf(name.split(" ")[0], name.split(" ").slice(1).join(" "));
  const links = menuLinksFor(permissions, user?.email || "");

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("myEmail");
    localStorage.removeItem("athleteEmail");
    localStorage.removeItem("expiresAt");
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} className="flex items-center gap-4">
        <span className="hidden bm-label-l text-bm-ink sm:inline">{name}</span>
        {user?.avatar ? (
          <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-bm-orange bm-label-xs text-white">{initials}</span>
        )}
      </button>
      {open && (
        <div role="menu" className="bm-fade-in absolute right-0 top-11 z-40 w-60 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl">
          <div className="px-4 pb-2 pt-1">
            <div className="bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted">{user?.email}</div>
          </div>
          <div className="border-t border-bm-border" />
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
              {l.label}
            </Link>
          ))}
          {links.length > 0 && <div className="border-t border-bm-border" />}
          <Link to={INDEX_PASSWORD_PATH} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Password
          </Link>
          <div className="border-t border-bm-border" />
          <button type="button" onClick={handleLogout} className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

const PlayerIndexLayout = ({ children }) => {
  const user = useSelector((state) => state.user.user || {});
  const permissions = user.permissions || [];
  const cacheShareTo = cacheShareLinkFor(permissions);

  return (
    <IndexShell
      homeTo={INDEX_HOME_PATH}
      right={(
        <>
          {cacheShareTo && (
            <Link
              to={cacheShareTo}
              className="hidden h-10 items-center rounded-full border border-bm-strong bg-white px-[18px] bm-label-l text-bm-ink transition-colors hover:bg-bm-subtle sm:inline-flex"
            >
              CacheShare
            </Link>
          )}
          <span className="hidden h-5 w-px bg-bm-border sm:inline" />
          <span className="hidden bm-overline md:inline">Player Index</span>
          <AvatarMenu user={user} permissions={permissions} />
        </>
      )}
    >
      {children ?? <Outlet />}
    </IndexShell>
  );
};

export default PlayerIndexLayout;
